import React from 'react';
import { Card, CardContent } from "../ui/card.jsx";
import { Badge } from "../ui/badge.jsx";
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import { createPageUrl } from '../../utils/index.ts';
import { AlertCircle, Clock, CheckCircle, Activity } from 'lucide-react';

export default function RecentTickets({ tickets, isLoading, isInternalUser }) {
  // Função auxiliar para formatar data de forma segura
  const formatDateSafely = (dateValue) => {
    try {
      if (!dateValue) return 'Data não disponível';
      
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return 'Data inválida';
      
      return format(date, "dd/MM/yyyy", { locale: ptBR });
    } catch (error) {
      console.warn('Erro ao formatar data:', dateValue, error);
      return 'Data inválida';
    }
  };

  const priorityColors = {
    low: 'bg-blue-100 text-blue-800',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-orange-100 text-orange-800',
    critical: 'bg-red-100 text-red-800'
  };
  
  const statusIcons = {
    open: <AlertCircle className="h-5 w-5 text-blue-500" />,
    in_progress: <Activity className="h-5 w-5 text-yellow-500" />,
    resolved: <CheckCircle className="h-5 w-5 text-green-500" />,
    closed: <CheckCircle className="h-5 w-5 text-gray-500" />,
    analysis: <Clock className="h-5 w-5 text-purple-500" />,
    reopened: <AlertCircle className="h-5 w-5 text-red-500" />
  };
  
  const statusLabels = {
    open: 'Aberto',
    in_progress: 'Em Progresso',
    resolved: 'Resolvido',
    closed: 'Fechado',
    analysis: 'Em Análise',
    reopened: 'Reaberto'
  };
  
  // Ordenar tickets por data de criação (mais recentes primeiro)
  const sortedTickets = tickets && tickets.length 
    ? [...tickets].sort((a, b) => {
        const dateA = new Date(a.created_at || a.created_date || 0);
        const dateB = new Date(b.created_at || b.created_date || 0);
        return dateB - dateA;
      }) 
    : [];
  
  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-0">
          <div className="animate-pulse">
            <div className="border-b p-4">
              <div className="h-5 bg-gray-200 w-1/3 rounded"></div>
            </div>
            {[1, 2, 3].map((_, i) => (
              <div key={i} className="border-b p-4 flex justify-between items-center">
                <div className="space-y-2 w-1/2">
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-100 rounded w-2/3"></div>
                </div>
                <div className="flex gap-2">
                  <div className="h-6 w-16 bg-gray-100 rounded-full"></div>
                  <div className="h-6 w-16 bg-gray-100 rounded-full"></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (!sortedTickets.length) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          <AlertCircle className="h-12 w-12 mx-auto mb-3 opacity-20" />
          <p className="text-lg">Nenhum chamado registrado.</p>
          <p className="text-sm mt-1">Os chamados recentes aparecerão aqui.</p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card>
      <CardContent className="p-0">
        <table className="w-full">
          <thead>
            <tr className="border-b">
              <th className="text-left p-4 font-medium text-muted-foreground">Título</th>
              <th className="text-left p-4 font-medium text-muted-foreground hidden md:table-cell">Status</th>
              <th className="text-left p-4 font-medium text-muted-foreground hidden md:table-cell">Prioridade</th>
              <th className="text-left p-4 font-medium text-muted-foreground hidden md:table-cell">Data</th>
              <th className="text-left p-4 font-medium text-muted-foreground hidden lg:table-cell">
                {isInternalUser ? 'Cliente' : 'Atribuído a'}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedTickets.slice(0, 5).map((ticket) => (
              <tr key={ticket.id} className="border-b hover:bg-muted/50 transition-colors">
                <td className="p-4">
                  <Link 
                    to={createPageUrl(`ViewTicket?id=${ticket.id}`)}
                    className="font-medium hover:text-primary transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      {statusIcons[ticket.status] || <AlertCircle className="h-4 w-4 text-gray-400" />}
                      <span>{ticket.title}</span>
                    </div>
                    <div className="md:hidden mt-1 flex flex-wrap gap-2">
                      <Badge variant="outline" className="text-xs">
                        {statusLabels[ticket.status] || ticket.status}
                      </Badge>
                      <Badge 
                        variant="secondary"
                        className={`text-xs ${priorityColors[ticket.priority] || 'bg-gray-100'}`}
                      >
                        {ticket.priority}
                      </Badge>
                    </div>
                  </Link>
                </td>
                <td className="p-4 hidden md:table-cell">
                  <Badge variant="outline">
                    {statusLabels[ticket.status] || ticket.status}
                  </Badge>
                </td>
                <td className="p-4 hidden md:table-cell">
                  <Badge 
                    variant="secondary"
                    className={priorityColors[ticket.priority] || 'bg-gray-100'}
                  >
                    {ticket.priority}
                  </Badge>
                </td>
                <td className="p-4 text-sm text-muted-foreground hidden md:table-cell">
                  {formatDateSafely(ticket.created_at || ticket.created_date)}
                </td>
                <td className="p-4 text-sm text-muted-foreground hidden lg:table-cell">
                  {isInternalUser 
                    ? ticket.company_name || 'Empresa' 
                    : ticket.assigned_to || 'Não atribuído'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}